import moment from "moment-timezone";
import { Content, TableCell } from "pdfmake/interfaces";
import { MonatsabschlussZeile } from "@/api/Abrechnung";
import { borderBottom, getOption, noBorder } from "../../pdfStyles";
import { Studio } from "@/api/Studio";

interface DamenMonatssummenBlatt {
  anzahlTageSteuerpflichtig: number;
  anzahlTage: number;
  pdfContent: Content[];
}

const createDameMonatsblatt = (
  zeile: MonatsabschlussZeile,
  studio: Studio,
  monatsname: string,
  anzahlTage: number
): DamenMonatssummenBlatt => {
  const monatssumme: DamenMonatssummenBlatt = {
    anzahlTageSteuerpflichtig: 0,
    anzahlTage: 0,
    pdfContent: [],
  };
  if (zeile.anzahlDiensteZusammengelegt === 0) {
    return monatssumme;
  }
  const eigeneSteuerAbrechnung = !!zeile.dame?.steuernummer;
  const tage = Array.from(Array(anzahlTage).keys()).map((n) => n + 1);
  const arbeitstage = zeile.tage
    ?.filter((abrechnungstag) => abrechnungstag.miete > 0)
    .reduce((result, tag) => {
      result[tag.buchungstag] = 1;
      return result;
    }, {} as any);
  const anzahlArbeitstage = Object.keys(arbeitstage).length;
  monatssumme.anzahlTage = anzahlArbeitstage;
  if (!eigeneSteuerAbrechnung) {
    monatssumme.anzahlTageSteuerpflichtig = anzahlArbeitstage;
  }
  
  monatssumme.pdfContent.push({
    text: "Anmeldung Dienstleister/in",
    style: "h2",
    pageBreak: "before",
  });
  monatssumme.pdfContent.push({
    layout: "monatsblattKopfdaten",
    margin: [0, -5, 20, 5],
    table: {
      widths: ["*"],
      headerRows: 0,
      keepWithHeaderRows: 0,
      body: [
        [
          {
            text: zeile.dame?.name,
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Name, Vorname)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.kuenstlername,
            style: "tableText2",
            margin: [0, 2, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Künstlername)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.geburtsdatum || "",
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Geburtsdatum)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.geburtsort || " ",
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Geburtsort)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.nationalitaet || " ",
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Nationalität)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.ausweisnummer || " ",
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Pass-/ Ausweisnr. *)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text: zeile.dame?.passaussteller || " ",
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Passaussteller)",
            style: "smaller",
            margin: [0, -4, 0, 16],
            border: noBorder,
          },
        ],
        [
          {
            text:
              "*) bei ausländischer Nationalität; zutreffendes ist zu unterstreichen",
            style: "smaller",
            margin: [0, -8, 0, 16],
            border: noBorder,
          },
        ],
      ],
    },
  });

  monatssumme.pdfContent.push({
    layout: "monatsblattOptionen",
    margin: [0, -5, 20, 5],
    table: {
      widths: [30, "*", 20, 70, 70, 110],
      headerRows: 0,
      keepWithHeaderRows: 0,
      body: [
        [
          getOption(eigeneSteuerAbrechnung),
          {
            text:
              'Ich nehme am vereinfachten (Vorauszahlungs-) Verfahren nicht teil.\n(siehe "Anlage Besteuerungsdaten bei Nichtteilnahme").',
            style: "tableText3",
            colSpan: 5,
            border: noBorder,
            margin: [0, -3, 0, 0],
          },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
        ],
        [
          getOption(!eigeneSteuerAbrechnung),
          {
            text:
              "Ich nehme am vereinfachten (Vorauszahlungs-) Verfahren teil und beauftrage den Betreiber die Tagespauschale an das Finanzamt abzuführen.",
            style: "tableText3",
            colSpan: 5,
            border: noBorder,
            margin: [0, -3, 0, 0],
          },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
          { text: "", border: noBorder },
        ],
        [
          getOption(eigeneSteuerAbrechnung),
          {
            text: "Meine Steuernummer lautet:",
            style: "tableText3",
            border: noBorder,
            margin: [0, 3, 0, 0],
          },
          {
            text: (eigeneSteuerAbrechnung && zeile.dame?.steuernummer) ? zeile.dame?.steuernummer : "",
            style: "tableText3",
            margin: [0, 3, 0, 0],
            border: borderBottom,
            alignment: "center",
            colSpan: 2,
          },
          { text: "", border: noBorder },
          {
            text: "bei Finanzamt:",
            style: "tableText3",
            alignment: "center",
            margin: [0, 3, 0, -3],
            border: noBorder,
          },
          {
            text: (eigeneSteuerAbrechnung && zeile.dame?.finanzamt) ? zeile.dame.finanzamt : "",
            style: "tableText3",
            margin: [0, 3, 0, 0],
            border: borderBottom,
            alignment: "center",
          },
        ],
        [
          getOption(eigeneSteuerAbrechnung),
          {
            text: "Meine Identifikationsnummer lautet:",
            style: "tableText3",
            border: noBorder,
            margin: [0, 3, 0, 0],
            colSpan: 2,
          },
          { text: "", border: noBorder },
          {
            text: (eigeneSteuerAbrechnung && zeile.dame?.identifikationsnummer) ? zeile.dame.identifikationsnummer : "",
            style: "tableText3",
            margin: [0, 3, 0, 0],
            border: borderBottom,
            colSpan: 3,
            alignment: "center",
          },
          { text: "", margin: [0, 3, 0, 0], border: noBorder },
          { text: "", margin: [0, 3, 0, 0], border: noBorder },
        ],
      ],
    },
  });

  const arbeitstageMarginsHeader = [0, 1, 0, 1];
  const arbeitstageMargins = [0, 3, 0, 1];
  monatssumme.pdfContent.push({
    layout: "solidLines",
    margin: [0, 25, 20, 5],
    table: {
      widths: ["*", ...tage.map((n) => (n < 10 ? 8 : 12)), 25],
      headerRows: 0,
      keepWithHeaderRows: 0,
      body: [
        [
          {
            text: "Monat",
            style: "small",
            alignment: "center",
            margin: arbeitstageMarginsHeader,
          },
          {
            text: "Datum",
            style: "small",
            alignment: "center",
            margin: arbeitstageMarginsHeader,
            colSpan: tage.length,
          },
          ...tage
            .slice(1)
            .map(
              (tag) =>
                ({ text: "", margin: arbeitstageMarginsHeader } as TableCell)
            ),
          {
            text: "Tage",
            style: "small",
            alignment: "center",
            margin: arbeitstageMarginsHeader,
          },
        ],
        [
          {
            text: monatsname,
            style: "small",
            alignment: "center",
            margin: arbeitstageMargins,
          },
          ...tage.map((n) => {
            const columns = [
              {
                text: "" + n,
                style: "small",
                alignment: "center",
                margin: [0, 0, 0, 0],
              },
            ] as TableCell[];
            if (arbeitstage[(n < 10 ? "0" : "") + n]) {
              (columns[0] as any).margin = n < 10 ? [1, 0, 0, 0] : [2, 0, 0, 0];
              columns.push({
                text: "X",
                fontSize: 13,
                color: "#444",
                margin: n < 10 ? [-6, -3, -1, -2] : [-8, -3, -1, -2],
              });
            }
            return { columns, margin: arbeitstageMargins } as TableCell;
          }),
          {
            text: "=  " + anzahlArbeitstage,
            style: "small",
            alignment: "center",
            margin: arbeitstageMargins,
          },
        ],
      ],
    },
  });

  monatssumme.pdfContent.push({
    layout: "monatsblattKopfdaten",
    margin: [0, -5, 20, 5],
    table: {
      widths: [80, 40, "*"],
      headerRows: 0,
      keepWithHeaderRows: 0,
      body: [
        [
          {
            text: "", //moment().format("DD.MM.yyyy"),
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
          {
            text: "",
            border: noBorder,
          },
          {
            text: "", //moment().format("DD.MM.yyyy"),
            style: "tableText2",
            margin: [0, 0, 0, -8],
            border: borderBottom,
          },
        ],
        [
          {
            text: "(Datum)",
            style: "smaller",
            border: noBorder,
            fontsize: 6,
            margin: [0, -2, 0, 0],
          },
          {
            text: "",
            style: "smaller",
            border: noBorder,
            fontsize: 6,
          },
          {
            text: "(Unterschrift)",
            style: "smaller",
            border: noBorder,
            fontsize: 6,
            margin: [0, -2, 0, 0],
          },
        ],
      ],
    },
  });

  return monatssumme;
};

export { DamenMonatssummenBlatt, createDameMonatsblatt };
